import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROOM_COLORS = {
  living_room: '#E8F5E9',
  bedroom: '#E3F2FD',
  kitchen: '#FFF3E0',
  bathroom: '#E0F7FA',
  dining_room: '#FFF8E1',
  office: '#F3E5F5',
  hallway: '#ECEFF1',
  garage: '#CFD8DC',
  laundry: '#E1F5FE',
  balcony: '#F1F8E9'
};

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;

const EXAMPLE_PROMPTS = [
  'Casa com 2 quartos, 1 banheiro, sala e cozinha americana',
  'Apartamento de 70m² com sala, 2 quartos, cozinha e banheiro',
  'Casa térrea com 3 quartos, 2 banheiros, sala de estar, cozinha e garagem',
  'Kitnet com quarto integrado, cozinha e banheiro'
];

const drawGrid = (ctx, width, height, gridSize) => {
  ctx.strokeStyle = '#E5E7EB';
  ctx.lineWidth = 0.5;
  for (let x = 0; x <= width; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let y = 0; y <= height; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }
};

const drawRooms = (ctx, rooms) => {
  if (!rooms) return;
  rooms.forEach((room) => {
    if (room.points && room.points.length > 2) {
      ctx.fillStyle = room.color || ROOM_COLORS[room.room_type] || '#E0E0E0';
      ctx.beginPath();
      ctx.moveTo(room.points[0].x, room.points[0].y);
      room.points.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      ctx.closePath();
      ctx.fill();

      const centerX = room.points.reduce((sum, p) => sum + p.x, 0) / room.points.length;
      const centerY = room.points.reduce((sum, p) => sum + p.y, 0) / room.points.length;
      ctx.fillStyle = '#374151';
      ctx.font = 'bold 12px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(room.name, centerX, centerY);
    }
  });
};

const drawWalls = (ctx, walls) => {
  if (!walls) return;
  ctx.strokeStyle = '#1F2937';
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  walls.forEach((wall) => {
    ctx.beginPath();
    ctx.moveTo(wall.start_x, wall.start_y);
    ctx.lineTo(wall.end_x, wall.end_y);
    ctx.stroke();
  });
};

const AIFloorPlanGenerator = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [floorPlan, setFloorPlan] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedProjectId, setSavedProjectId] = useState(null);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !floorPlan) return;
    const ctx = canvas.getContext('2d');

    // Clear
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const gridSize = floorPlan.scale || 20;
    drawGrid(ctx, CANVAS_WIDTH, CANVAS_HEIGHT, gridSize);
    drawRooms(ctx, floorPlan.rooms);
    drawWalls(ctx, floorPlan.walls);
  }, [floorPlan]);

  useEffect(() => {
    if (floorPlan) {
      drawCanvas();
    }
  }, [floorPlan, drawCanvas]);

  const generateFloorPlan = async () => {
    if (!description.trim()) {
      setError('Por favor, descreva a casa que deseja criar');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await axios.post(`${API}/generate-floor-plan`, {
        description: description,
        language: 'pt'
      });

      setFloorPlan(response.data.floor_plan);
      setSavedProjectId(null); // Reset saved project ID
    } catch (error) {
      reportClientError('Error generating floor plan:', error);
      setError('Erro ao gerar planta. Tente novamente com uma descrição mais detalhada.');
    } finally {
      setGenerating(false);
    }
  };

  const saveProject = async () => {
    if (!projectName || !userEmail) {
      alert('Por favor, preencha nome do projeto e email');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/projects`, {
        name: projectName,
        user_email: userEmail,
        user_name: userName,
        description: description,
        floor_plan: floorPlan
      });

      setSavedProjectId(response.data.id);
      setShowSaveModal(false);
      alert('Projeto salvo com sucesso!');
    } catch (error) {
      reportClientError('Error saving project:', error);
      alert('Erro ao salvar projeto');
    } finally {
      setSaving(false);
    }
  };

  const view3D = () => {
    if (savedProjectId) {
      navigate(`/render-3d/${savedProjectId}`);
    } else if (floorPlan) {
      setShowSaveModal(true);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-300 hover:text-white"
              data-testid="ai-floorplan-back-button"
            >
              ← Voltar
            </button>
            <h1 className="text-xl font-bold text-white">🤖 Gerador de Planta com IA</h1>
          </div>
          {floorPlan && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowSaveModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                data-testid="ai-floorplan-save-button"
              >
                💾 Salvar
              </button>
              <button
                onClick={view3D}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                data-testid="ai-floorplan-view-3d-button"
              >
                🏠 Ver em 3D
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-4">Descreva sua casa</h2>
              <p className="text-gray-300 text-sm mb-4">
                Nossa IA irá criar uma planta baixa baseada na sua descrição. Seja o mais detalhado possível!
              </p>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Casa com 3 quartos, sendo 1 suíte, 2 banheiros, sala de estar ampla, cozinha americana integrada, área de serviço e garagem para 2 carros..."
                className="w-full h-40 p-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                data-testid="ai-floorplan-description-textarea"
              />
              
              {error && (
                <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300" data-testid="ai-floorplan-error-message">
                  {error}
                </div>
              )}

              <button
                onClick={generateFloorPlan}
                disabled={generating || !description.trim()}
                className="w-full mt-4 py-4 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="ai-floorplan-generate-button"
              >
                {generating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Gerando planta...
                  </>
                ) : (
                  <>
                    🚀 Gerar Planta com IA
                  </>
                )}
              </button>
            </div>

            {/* Example Prompts */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">💡 Exemplos de descrição</h3>
              <div className="space-y-2">
                {EXAMPLE_PROMPTS.map((prompt, index) => (
                  <button
                    key={prompt}
                    onClick={() => setDescription(prompt)}
                    className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg text-gray-300 text-sm transition border border-white/10"
                    data-testid={`ai-floorplan-example-prompt-button-${index}`}
                  >
                    "{prompt}"
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Preview da Planta</h2>
            
            {floorPlan ? (
              <div>
                <canvas
                  ref={canvasRef}
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  className="w-full bg-white rounded-lg"
                  data-testid="ai-floorplan-preview-canvas"
                />
                <div className="mt-4 text-center">
                  <p className="text-gray-300 text-sm">
                    {floorPlan.rooms?.length || 0} cômodos • {floorPlan.walls?.length || 0} paredes
                  </p>
                  <button
                    onClick={view3D}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-blue-700 transition-all"
                    data-testid="ai-floorplan-preview-view-3d-button"
                  >
                    🏠 Ver sua casa em 3D
                  </button>
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-white/5 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <div className="text-6xl mb-4">🏗️</div>
                  <p>Sua planta aparecerá aqui</p>
                  <p className="text-sm mt-2">Descreva sua casa e clique em "Gerar"</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Salvar Projeto</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Projeto *</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Ex: Minha Casa dos Sonhos"
                  data-testid="ai-floorplan-project-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seu Email *</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="seu@email.com"
                  data-testid="ai-floorplan-user-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Seu Nome</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Seu nome completo"
                  data-testid="ai-floorplan-user-name-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 px-4 py-3 border rounded-lg hover:bg-gray-50"
                data-testid="ai-floorplan-save-cancel-button"
              >
                Cancelar
              </button>
              <button
                onClick={saveProject}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                data-testid="ai-floorplan-save-submit-button"
              >
                {saving ? 'Salvando...' : 'Salvar e Ver 3D'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIFloorPlanGenerator;
